import React from 'react';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { NotificationType } from '../graphql/generated/graphql';
import { useGetUser } from '../user/useAllUsers';
import Logger from '../utils/Logger';
import truncate from '../utils/text/truncate';
import NotificationCommentPreview from './NotificationCommentPreview';
import * as styles from './NotificationMessage.module.scss';

const TRUNCATE_LENGTH = 50;
const MESSAGE_TRUNCATE_LENGTH = 200;

const NOTIFICATIONS_FRAGMENT = gql(`
  fragment NotificationMessage_notifications on Notification {
    id
    type
    actor {
      ...useGetUser_user
    }
    ... on CommentNotification {
      comment {
        id
        thread {
          id
          post {
            id
            title
          }
        }
      }
    }
    ... on HistoryNotification {
      history {
        post {
          id
          title
        }
      }
    }
    ... on PostNotification {
      post {
        id
        title
      }
    }
    ... on ReactionNotification {
      reaction {
        thread {
          post {
            id
            title
          }
        }
      }
    }
    metadata {
      message
    }
    ...NotificationCommentPreview_notification
  }
`);

interface NotificationMessageProps {
  notifications: DocumentType<typeof NOTIFICATIONS_FRAGMENT>[];
}

const NotificationMessage = ({ notifications }: NotificationMessageProps) => {
  const notification = notifications[0];
  const getUser = useGetUser();

  if (!notification) {
    return null;
  }

  const { name } = getUser(notification.actor);

  const getTruncated = (text?: string | null, length = TRUNCATE_LENGTH) => {
    return typeof text === 'string' ? truncate(text, length) : '';
  };

  switch (notification.type) {
    case NotificationType.CommentCreate:
      return (
        <>
          {notifications.length > 1
            ? `${name} left ${notifications.length} comments on`
            : `${name} commented on`}{' '}
          <strong>
            {getTruncated(
              'comment' in notification
                ? notification.comment?.thread.post.title
                : null,
            )}
          </strong>
          {notifications.map((n) => (
            <NotificationCommentPreview key={n.id} notification={n} />
          ))}
        </>
      );
    case NotificationType.CommentMention:
      return (
        <>
          {notifications.length > 1
            ? `${name} mentioned you in ${notifications.length} comments in`
            : `${name} mentioned you in a comment in`}{' '}
          <strong>
            {getTruncated(
              'comment' in notification
                ? notification.comment?.thread.post.title
                : null,
            )}
          </strong>
          {notifications.map((n) => (
            <NotificationCommentPreview key={n.id} notification={n} />
          ))}
        </>
      );
    case NotificationType.CommentReply: {
      const [oldest, ...additionalNotifications] = notifications.toReversed();

      return (
        <>
          {notifications.length > 1
            ? `${name} left ${notifications.length} replies on your comment in`
            : `${name} replied to your comment in`}{' '}
          <strong>
            {getTruncated(
              'comment' in notification
                ? notification.comment?.thread.post.title
                : null,
            )}
          </strong>
          {oldest && <NotificationCommentPreview notification={oldest} />}
          {additionalNotifications.map((n) => (
            <NotificationCommentPreview
              key={n.id}
              notification={n}
              hideContext
            />
          ))}
        </>
      );
    }
    case NotificationType.CommentResolve: {
      return (
        <>
          {notifications.length > 1
            ? `${name} resolved ${notifications.length} comments in`
            : `${name} resolved your comment in`}{' '}
          <strong>
            {getTruncated(
              'comment' in notification
                ? notification.comment?.thread.post.title
                : null,
            )}
          </strong>
          {notifications.map((n) => (
            <NotificationCommentPreview key={n.id} notification={n} />
          ))}
        </>
      );
    }
    case NotificationType.InviteAccept:
      return <>{name} accepted your invitation to Slab!</>;
    case NotificationType.PostEdit:
      return (
        <>
          {name} edited{' '}
          <strong>
            {getTruncated(
              'history' in notification
                ? notification.history?.post.title
                : null,
            )}
          </strong>
        </>
      );
    case NotificationType.PostArchive:
      return (
        <>
          {name} archived your post{' '}
          <strong>
            {'post' in notification && getTruncated(notification.post?.title)}
          </strong>
        </>
      );
    case NotificationType.PostPublish:
      return (
        <>
          {name} published{' '}
          <strong>
            {getTruncated(
              'post' in notification ? notification.post?.title : null,
            )}
          </strong>
        </>
      );
    case NotificationType.PostShare:
      return (
        <>
          {name} shared{' '}
          <strong>
            {getTruncated(
              'post' in notification ? notification.post?.title : null,
            )}
          </strong>
        </>
      );
    case NotificationType.PostThank:
      return (
        <>
          {name} thanked you for{' '}
          <strong>
            {getTruncated(
              'post' in notification ? notification.post?.title : null,
            )}
          </strong>
        </>
      );
    case NotificationType.ReactionCreate:
      return (
        <>
          {name} reacted to content in{' '}
          <strong>
            {getTruncated(
              'reaction' in notification
                ? notification.reaction?.thread.post.title
                : null,
            )}
          </strong>
        </>
      );
    case NotificationType.VerificationExpired:
      return (
        <>
          Verification has expired for{' '}
          <strong>
            {getTruncated(
              'post' in notification ? notification.post?.title : null,
            )}
          </strong>
          . Reverify to restore its badge.
        </>
      );
    case NotificationType.VerificationReminder:
      return (
        <>
          Verification for{' '}
          <strong>
            {getTruncated(
              'post' in notification ? notification.post?.title : null,
            )}
          </strong>{' '}
          will expire soon. Reverify to keep its badge.
        </>
      );
    case NotificationType.VerificationRequest:
      return (
        <>
          {name} requested that you verify{' '}
          <strong>
            {getTruncated(
              'post' in notification ? notification.post?.title : null,
            )}
          </strong>
          {notification.metadata.message && (
            <div className={styles.actorMessage}>
              {getTruncated(
                notification.metadata.message,
                MESSAGE_TRUNCATE_LENGTH,
              )}
            </div>
          )}
        </>
      );
    default:
      Logger.error('Unrecognized notification', notification);
      return null;
  }
};

export default NotificationMessage;
